import { supabase } from '@/integrations/supabase/client';
import type { EmailOtpType } from '@supabase/supabase-js';

// Shared handler for every transactional-auth-email landing (confirm signup,
// reset password, change email). It is BACKWARD COMPATIBLE across all three link
// formats so that changing the Supabase email templates (a separate dashboard
// step) never strands links already sitting in inboxes:
//
//   1. ?token_hash=&type=   -> verifyOtp({ token_hash, type }). No code_verifier
//                              needed, so this is the ONLY format that survives
//                              cross-device / cross-browser and the iOS webview
//                              (signup in WKWebView, confirm in Safari). This is
//                              the whole point of the change.
//   2. ?code=               -> a PKCE link. detectSessionInUrl (still enabled for
//                              OAuth) exchanges it at client-init; we confirm the
//                              resulting session via getSession rather than
//                              re-consuming the one-time code. Same-browser only.
//   3. #access_token=&type= -> a legacy implicit link. detectSessionInUrl sets the
//                              session at init; confirmed via getSession.

export type AuthCallbackErrorKind = 'expired' | 'used' | 'invalid' | 'unknown';

export interface AuthCallbackResult {
  status: 'success' | 'error' | 'none';
  type?: string;
  errorKind?: AuthCallbackErrorKind;
  message?: string;
}

function classifyOtpError(err: { code?: string; message?: string } | null): AuthCallbackErrorKind {
  const code = (err?.code ?? '').toLowerCase();
  const msg = (err?.message ?? '').toLowerCase();
  if (code.includes('expired') || msg.includes('expired')) return 'expired';
  if (msg.includes('already') || msg.includes('used') || code.includes('used')) return 'used';
  if (code.includes('invalid') || msg.includes('invalid') || msg.includes('not found')) return 'invalid';
  return 'unknown';
}

// Remove ONLY the auth params from the current URL so a refresh cannot re-verify a
// one-time token, while preserving everything else (e.g. ?intent=, ?next=).
function stripAuthParams(): void {
  const url = new URL(window.location.href);
  ['token_hash', 'type', 'code'].forEach((k) => url.searchParams.delete(k));
  const qs = url.searchParams.toString();
  window.history.replaceState({}, '', url.pathname + (qs ? `?${qs}` : ''));
}

export async function consumeAuthCallback(): Promise<AuthCallbackResult> {
  const url = new URL(window.location.href);
  const tokenHash = url.searchParams.get('token_hash');
  const type = url.searchParams.get('type') ?? undefined;
  const hasCode = url.searchParams.has('code');
  const hasHashToken = (window.location.hash || '').includes('access_token');

  // 1. token_hash: cross-device path.
  if (tokenHash && type) {
    const { error } = await supabase.auth.verifyOtp({
      token_hash: tokenHash,
      type: type as EmailOtpType,
    });
    stripAuthParams();
    if (error) {
      return { status: 'error', type, errorKind: classifyOtpError(error), message: error.message };
    }
    return { status: 'success', type };
  }

  // 2 + 3. code / hash already handled by detectSessionInUrl at client-init; do not
  // re-consume the one-time code, just confirm the resulting session.
  if (hasCode || hasHashToken) {
    const { data: { session } } = await supabase.auth.getSession();
    stripAuthParams();
    if (session) return { status: 'success', type };
    // No session means a PKCE link was opened in a different browser than the one
    // that requested it (no code_verifier) -- exactly what token_hash fixes.
    return {
      status: 'error',
      type,
      errorKind: 'invalid',
      message: 'This link could not be verified in this browser. Request a new one.',
    };
  }

  return { status: 'none' };
}
